import { now } from "@@/shared/date-time/date-time";
import { isoAndUnixZodSchema } from "@@/shared/date-time/iso-and-unix";
import type { DurationInHours } from "@@/shared/date-time/time-spans";
import { inHours } from "@@/shared/date-time/time-spans";
import { generateId } from "@@/utilities/generate-id";
import { addHours } from "date-fns";
import type { Except } from "type-fest";
import * as z from "zod";
import { userIdZodSchema } from "../user-id";
import {
    verificationIdFactory,
    verificationIdZodSchema,
} from "./verification-id";

const emailVerificationZodObject = z.object({
    _id: verificationIdZodSchema,
    _createdAt: isoAndUnixZodSchema,
    _type: z.literal("VERIFICATION"),
    type: z.literal("EMAIL"),
    verificationCode: z.string().min(20),
    email: z.string().email(),
    userId: userIdZodSchema,
    expiresAt: z.date(),
});
type EmailVerification = z.infer<typeof emailVerificationZodObject>;
const emailVerificationZodSchema: z.ZodSchema<EmailVerification> =
    emailVerificationZodObject;

type FactoryParams = Except<
    EmailVerification,
    "_id" | "_createdAt" | "_type" | "type" | "expiresAt" | "verificationCode"
> & { expiration?: DurationInHours; verificationCode?: string };

const emailVerificationFactory = (params: FactoryParams): EmailVerification => {
    const expiration: DurationInHours =
        params.expiration ?? inHours({ days: 2 });
    const emailVerification: EmailVerification = {
        _id: verificationIdFactory(),
        _createdAt: now(),
        _type: "VERIFICATION",
        type: "EMAIL",
        ...params,
        verificationCode: params.verificationCode ?? generateId(),
        expiresAt: addHours(new Date(), expiration),
    };
    return emailVerification;
};

export {
    emailVerificationFactory,
    emailVerificationZodObject,
    emailVerificationZodSchema,
};
export type { EmailVerification };
